import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";

export const useStudyGroupMemberships = (
  studyGroupId: string | number | null,
  perPage = 20,
  initialPage = 1,
  keyword?: string,
  membershipStatus?: number,
) => {
  const [page, setPage] = useState<number>(initialPage);
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    keyword,
  );

  // React Queryでメンバーシップ取得
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "studyGroupMemberships",
      studyGroupId,
      page,
      perPage,
      searchKeyword ?? "",
      membershipStatus ?? null,
    ], // objectを避けプリミティブで安定化
    queryFn: () => {
      if (!studyGroupId) {
        throw new Error("Study group ID is null");
      }
      return studyGroupsApi.getStudyGroupMemberships(
        studyGroupId,
        perPage,
        page,
        searchKeyword,
        membershipStatus,
      );
    },
    enabled: !!studyGroupId, // studyGroupIdがある場合のみクエリ実行
    staleTime: 3 * 60 * 1000, // メンバーシップは3分間キャッシュ
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // ページ取得関数（既存インターフェース維持）
  const fetchPage = useCallback(
    async (newPage = 1, newSearchKeyword?: string) => {
      setPage(newPage);
      if (newSearchKeyword !== undefined) {
        setSearchKeyword(newSearchKeyword);
      }
      // setPageとsetSearchKeywordが完了すると、useQueryのqueryKeyが変わり自動で新しいクエリが実行される
    },
    [],
  );

  // 検索機能
  const searchMembers = useCallback((newKeyword?: string) => {
    setSearchKeyword(newKeyword);
    setPage(1); // 検索時はページを1に戻す
  }, []);

  // ページ変更
  const changePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    data: data?.results || [],
    pagination: data
      ? {
          total: data.total,
          page: data.page,
          pages: data.pages,
          perPage,
        }
      : undefined,
    isLoading,
    error: queryError ? String(queryError) : null,
    fetchPage, // 既存インターフェース維持
    searchMembers, // 新しく追加：検索機能
    changePage, // 新しく追加：ページ変更
    refetch: () => refetch(), // 手動リフェッチ
    currentKeyword: searchKeyword, // 新しく追加：現在の検索キーワード
    currentPage: page, // 新しく追加：現在のページ
  };
};
